import { Typography } from '@musinsa/mds';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { SeedingProduct } from '../types';
import { CampaignInfo } from './CampaignInfo';

/**
 * 상품 카테고리 그룹 영역 — ProductCard(개별 카드)와 분리된 그룹 단위 컴포넌트.
 *
 * - **ProductCardSectionTitle**: "상품 N개" / "제공 상품 N개" 섹션 헤더 (그룹들 위 1회).
 * - **ProductCardGroup**: 카테고리 그룹 한 개.
 *   - `Container`: 그룹 컨테이너(Title + List). (그룹 간 간격은 바깥 wrapper 담당)
 *   - `Title`: 카테고리 헤더.
 *   - `List`: 그룹 내 ProductCard 들 — 카드 간 간격(16px) 책임.
 *
 * 그룹 간 간격은 그룹들을 감싸는 바깥 wrapper(CampaignInfo.Container 의 gap-y)가 담당한다.
 * 데이터는 `groupProductCards(products)` 로 카테고리 그룹 배열로 변환해 사용한다.
 *
 * ```tsx
 * <CampaignInfo.Container>          {/* 그룹 간 gap 담당 *\/}
 *   <ProductCardSectionTitle count={products.length} guideText="..." />
 *   {groupProductCards(products).map((group, i) => (
 *     <ProductCardGroup.Container key={i}>
 *       {group.category && <ProductCardGroup.Title>{group.category}</ProductCardGroup.Title>}
 *       <ProductCardGroup.List>
 *         {group.items.map((p) => (
 *           <ProductCard key={p.productId} soldOut={p.soldOut}>…</ProductCard>
 *         ))}
 *       </ProductCardGroup.List>
 *     </ProductCardGroup.Container>
 *   ))}
 * </CampaignInfo.Container>
 * ```
 */

/** 카테고리 그룹 단위. `category === ''` 이면 헤더 미노출. */
export interface ProductCardGroupData {
  category: string;
  items: SeedingProduct[];
}

/**
 * 상품 목록을 카테고리 연속 묶음으로 그룹화한다.
 * `mapGoodsToProducts` 가 연속 동일 카테고리를 첫 상품만 남기고 ''(빈 문자열) 로 dedup 하므로,
 * 비어있지 않은 category 가 새 그룹을 시작하고 '' 는 직전 그룹에 이어 붙인다.
 */
export const groupProductCards = (products: SeedingProduct[]): ProductCardGroupData[] => {
  const groups: ProductCardGroupData[] = [];
  for (const product of products) {
    const current = groups[groups.length - 1];
    if (product.category !== '' || !current) {
      groups.push({ category: product.category, items: [product] });
    } else {
      current.items.push(product);
    }
  }
  return groups;
};

interface SectionTitleProps {
  count: number;
  /** 안내 문구(섹션 타이틀 하단). */
  guideText?: string;
  /** guideText 색상 emphasis. 'red' = 품절 안내. */
  guideTextEmphasis?: 'red' | 'default';
  /**
   * 타이틀 prefix — `true` = "제공 상품 N개"(신청/수락 전), `false` = "상품 N개"(신청/수락 완료 이후).
   * 적색 품절 안내(guideTextEmphasis)와 무관하게 stage 로 판단한다 (SID-4317).
   * 미지정 시 guideText 유무로 추론(레거시: 제안형).
   */
  provided?: boolean;
  className?: string;
}

/** 상품 섹션 타이틀 — "상품 N개" / "제공 상품 N개" + 안내. (그룹들 위에 1회 노출) */
export const ProductCardSectionTitle = ({
  count,
  guideText,
  guideTextEmphasis = 'default',
  provided,
  className,
}: SectionTitleProps) => (
  <div className={twMerge('flex flex-col gap-[4px]', className)}>
    <CampaignInfo.Title>
      {(provided ?? Boolean(guideText)) ? `제공 상품 ${count}개` : `상품 ${count}개`}
    </CampaignInfo.Title>
    {guideText && (
      <Typography
        variant="body13pxReg"
        colorScheme={guideTextEmphasis === 'red' ? 'red' : 'gray-600'}
      >
        {guideText}
      </Typography>
    )}
  </div>
);

interface GroupContainerProps {
  /** 한 그룹 = Title(카테고리) + List(카드들). */
  children: ReactNode;
  className?: string;
}

/** 카테고리 그룹 한 개 컨테이너 — Title + List 를 묶는다. (그룹 간 간격은 바깥 wrapper 담당) */
const GroupContainer = ({ children, className }: GroupContainerProps) => (
  <div className={twMerge('flex flex-col', className)}>{children}</div>
);

interface GroupTitleProps {
  children: ReactNode;
  /** 그룹 전체 품절 시 dim. */
  soldOut?: boolean;
  className?: string;
}

/** 그룹 카테고리 헤더 — 카테고리 ↔ 카드 사이 8px. */
const GroupTitle = ({ children, soldOut, className }: GroupTitleProps) => (
  <Typography
    variant="body13pxSemi"
    colorScheme={soldOut ? 'gray-600' : 'black'}
    className={twMerge('mb-[8px]!', className)}
  >
    {children}
  </Typography>
);

interface GroupListProps {
  /** 그룹 내 ProductCard 들. */
  children: ReactNode;
  className?: string;
}

/** 그룹 내 카드 목록 — 카드 간 간격(item gap) 16px. */
const GroupList = ({ children, className }: GroupListProps) => (
  <div className={twMerge('flex flex-col gap-[16px]!', className)}>{children}</div>
);

/** 카테고리 그룹 단위 — Container(한 그룹) + Title(카테고리) + List(그룹 내 카드들). */
export const ProductCardGroup = {
  Container: GroupContainer,
  Title: GroupTitle,
  List: GroupList,
};
